// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";

const GlobalAdminHeader = ({
  title,
  subtitle,
  status,
  statusColor = "green",
  buttons = [],
  tenantOptions = [],
  selectedTenant,
  onTenantChange,
  tenantsLoading = false,
  ranges = [],
  selectedRange,
  onRangeChange,
  className = "",
}) => {
  const getStatusStyles = (color) => {
    const statusStyles = {
      green: {
        bg: "bg-green-50 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-300",
        border: "border-green-200 dark:border-green-700",
        dot: "bg-green-500",
      },
      red: {
        bg: "bg-red-50 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-300",
        border: "border-red-200 dark:border-red-700",
        dot: "bg-red-500",
      },
      yellow: {
        bg: "bg-yellow-50 dark:bg-yellow-900/30",
        text: "text-yellow-700 dark:text-yellow-300",
        border: "border-yellow-200 dark:border-yellow-700",
        dot: "bg-yellow-500",
      },
      blue: {
        bg: "bg-blue-50 dark:bg-blue-900/30",
        text: "text-blue-700 dark:text-blue-300",
        border: "border-blue-200 dark:border-blue-700",
        dot: "bg-blue-500",
      },
    };
    return statusStyles[color] || statusStyles.green;
  };

  const statusStyles = getStatusStyles(statusColor);

  return (
    <div
      className={`bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm ${className}`}
    >
      <div className="mx-auto px-4 lg:px-6 py-5 flex flex-col lg:flex-row items-start gap-2 lg:items-center justify-between">
        {/* Left side - Title and Subtitle */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">
            {title}
          </h1>
          {subtitle && (
            <div className="text-sm md:text-base text-slate-600 dark:text-slate-400 mt-1">
              {subtitle}
            </div>
          )}
        </div>

        {/* Right side - Status, Controls, and Buttons */}
        <div className="flex items-center gap-3 flex-wrap lg:justify-end">
          {/* Status Badge */}
          {status && (
            <div
              className={`flex items-center gap-2 ${statusStyles.bg} ${statusStyles.text} px-3 py-1 rounded-full border ${statusStyles.border}`}
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${statusStyles.dot}`}
              />
              <span className="text-sm font-medium">{status}</span>
            </div>
          )}

          {/* Tenant Selector */}
          {tenantOptions.length > 0 && (
            <select
              className="control-select text-sm border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 dark:text-slate-200"
              value={selectedTenant}
              onChange={(e) => onTenantChange && onTenantChange(e.target.value)}
              disabled={tenantsLoading}
            >
              {tenantOptions.map((tenant) => (
                <option key={tenant.value} value={tenant.value}>
                  {tenant.label}
                </option>
              ))}
            </select>
          )}

          {/* Range Selector */}
          {ranges.length > 0 && (
            <select
              className="control-select text-sm border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 dark:text-slate-200"
              value={selectedRange}
              onChange={(e) => onRangeChange && onRangeChange(e.target.value)}
            >
              {ranges.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          {/* Dynamic Buttons */}
          {buttons.map((button, index) =>
            button.dropdown ? (
              <div key={index} className="relative">
                <button
                  type="button"
                  className={`control-btn ${button.className || "bg-primary-color text-white px-4 py-2 rounded-md hover:opacity-90"} text-sm`}
                  onClick={button.onDropdownToggle}
                  disabled={button.disabled}
                  title={button.tooltip}
                  style={{ gap: "0px" }}
                >
                  {button.icon && <span className="mr-2">{button.icon}</span>}
                  {button.text}
                </button>
                {button.dropdownOpen && (
                  <div
                    className="absolute left-0 top-full z-50 mt-1 min-w-[250px] rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 py-1 shadow-lg"
                    role="menu"
                  >
                    {(button.dropdownItems || []).map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        role="menuitem"
                        onClick={() => button.onDropdownSelect(item.value)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        {item.icon && (
                          <span className="text-gray-500 dark:text-slate-400">
                            {item.icon}
                          </span>
                        )}
                        {item.label}
                      </button>
                    ))}
                    {button.dropdownFooter}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={index}
                type="button"
                className={`control-btn ${button.className || "bg-primary-color text-white px-4 py-2 rounded-md hover:opacity-90"} text-sm`}
                onClick={button.onClick}
                disabled={button.disabled}
                title={button.tooltip}
                style={{ gap: "0px" }}
              >
                {button.icon && <span className="mr-2">{button.icon}</span>}
                {button.text}
              </button>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalAdminHeader;
